import React, { forwardRef } from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Breadcrumbs as MuiBreadcrumbs } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!

 Explicit props based on actual usage in the codebase.
**/
export interface BreadcrumbsProps {
  /** The breadcrumb items (typically links and a current-page label). */
  children?: React.ReactNode;
  /** Separator node rendered between breadcrumb items. Defaults to a small "next" chevron. */
  separator?: React.ReactNode;
  /** Accessible label for the breadcrumb navigation landmark. */
  'aria-label'?: string;
  /** Inline styles applied to the root element. */
  style?: React.CSSProperties;
  /** CSS class name applied to the root element. */
  className?: string;
}

const Breadcrumbs = forwardRef<HTMLElement, BreadcrumbsProps>(
  (
    {
      children,
      separator = <NavigateNextIcon fontSize="small" />,
      'aria-label': ariaLabel,
      style,
      className,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Breadcrumbs: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the BreadcrumbsProps interface and to the component's signature.`,
        true
      );
    }

    return (
      <MuiBreadcrumbs
        ref={ref}
        separator={separator}
        aria-label={ariaLabel}
        style={style}
        className={className}
        {...restProps}
      >
        {children}
      </MuiBreadcrumbs>
    );
  }
);

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
